import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  CircularProgress,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import { useDispatch, useSelector } from "react-redux";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { fetchEmpAllocation } from "../../actions/EmpAllocationAction";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import homeImage from "../../assets/DTS2.png";

/* ------------ Styles ------------ */

const PageContainer = styled(Box)(() => ({
  maxWidth: "900px",
  margin: "0 auto",
  padding: "10px 15px",
  fontFamily: "'Poppins', sans-serif",
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: "16px",
  boxShadow: "0px 6px 15px rgba(0,0,0,0.12)",
  border: "1px solid #d6dce5",
  backgroundColor: "#ffffff",
  maxHeight: "650px",
  overflowY: "auto",
  overflowX: "auto",
  position: "relative",
  "& .MuiTable-root": {
    borderCollapse: "separate",
    minWidth: "100%",
  },
  "&::-webkit-scrollbar": {
    width: "8px",
    height: "8px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#888",
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "#555",
  },
  [theme.breakpoints.down("sm")]: {
    borderRadius: "12px",
    maxHeight: "calc(100vh - 380px)",
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontFamily: "'Poppins', sans-serif",
  padding: "16px 12px",
  fontSize: "15px",
  textAlign: "center",
  whiteSpace: "nowrap",
  "&.MuiTableCell-head": {
    backgroundColor: "#154c79",
    color: "#ffffff",
    fontWeight: 600,
    letterSpacing: "0.5px",
    borderBottom: "none",
    position: "sticky",
    top: 0,
    zIndex: 10,
    fontSize: "15px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "10px 8px",
    fontSize: "13px",
    whiteSpace: "normal",
    wordBreak: "break-word",
    lineHeight: "1.5",
    "&.MuiTableCell-head": {
      fontSize: "13px",
      padding: "12px 8px",
      lineHeight: "1.4",
    },
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  transition: "0.25s ease",
  "&:nth-of-type(odd)": {
    backgroundColor: "rgba(243, 246, 250, 0.6)",
  },
  "&:hover": {
    backgroundColor: "rgba(14, 60, 102, 0.09)",
    cursor: "pointer",
  },
}));

export default function EmployeeAllocationMain() {
  const dispatch = useDispatch();
  const { loading, data } = useSelector((state) => state.empAllocation);

  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("allocation");

  const formatDate = (dateObj) => {
    const d = new Date(dateObj);
    return d.toISOString().split("T")[0];
  };

  const formatTime = (dateObj) => {
    const d = new Date(dateObj);
    return d.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  useEffect(() => {
    const today = new Date();
    setSelectedDate(today);
    const formattedToday = formatDate(today);
    dispatch(fetchEmpAllocation(formattedToday));
  }, [dispatch]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setOpenDatePicker(false);
    const formatted = formatDate(date);
    dispatch(fetchEmpAllocation(formatted));
  };

  const handleViewModeChange = (event) => {
    setViewMode(event.target.value);
  };

  const filteredData =
    data?.filter((item) =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    ) || [];

  // Group by Task for allocation view
  const groupedByTask = filteredData.reduce((acc, item) => {
    const task = item.CDLjob;
    if (!acc[task]) {
      acc[task] = {
        CDLjob: task,
        countIWOno: 0,
      };
    }
    acc[task].countIWOno += Number(item.countIWOno || 0);
    return acc;
  }, {});

  // Group by Supervisor
  const groupedBySupervisor = filteredData.reduce((acc, item) => {
    const supervisor = item.Supervisorname;
    if (!acc[supervisor]) {
      acc[supervisor] = {
        Supervisorname: supervisor,
        LocCode: item.LocCode,
        countIWOno: 0,
      };
    }
    acc[supervisor].countIWOno += Number(item.countIWOno || 0);
    return acc;
  }, {});

  const taskArray = Object.values(groupedByTask).sort((a, b) => {
    const extractCode = (job) =>
      (job || "")
        .split("/")[0]
        .trim()
        .toUpperCase();

    const codeA = extractCode(a.CDLjob);
    const codeB = extractCode(b.CDLjob);
    const priority = ["NC", "SR", "OR"];

    const aIndex = priority.indexOf(codeA);
    const bIndex = priority.indexOf(codeB);
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    return a.CDLjob.localeCompare(b.CDLjob);
  });

  const supervisorArray = Object.values(groupedBySupervisor);

  const totalEmployees =
    filteredData.reduce((sum, item) => sum + Number(item.countIWOno || 0), 0) || 0;

  const getCurrentData = () => {
    if (viewMode === "allocation") return taskArray;
    if (viewMode === "supervisor") return supervisorArray;
    return filteredData;
  };

  const currentData = getCurrentData();

  // PDF EXPORT FUNCTION
  const handleExportPDF = () => {
    const doc = new jsPDF("p", "mm", "a4");
    const generatedAt = new Date();
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.addImage(homeImage, "PNG", 0, 0, 210, 65);

    let title = "";
    if (viewMode === "allocation") {
      title = "Employee Allocation - CDL Jobs Summary";
    } else if (viewMode === "supervisor") {
      title = "Employee Allocation - Supervisor Summary";
    } else {
      title = "Employee Allocation - Details";
    }

    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(21, 76, 121);
    doc.text(title, pageWidth / 2, 75, { align: "center" });

    doc.setDrawColor(21, 76, 121);
    doc.setLineWidth(0.5);
    doc.line(15, 82, pageWidth - 15, 82);

    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(60, 60, 60);

    const startX = 15;
    const endX = pageWidth - 15;
    const yPosition = 88;

    doc.setFont("helvetica", "bold");
    doc.text("Date - ", startX, yPosition);
    doc.setFont("helvetica", "normal");
    doc.text(formatDate(selectedDate), startX + doc.getTextWidth("Date -  "), yPosition);

    doc.setFont("helvetica", "bold");
    const timeLabel = "Time - ";
    const timeValue = formatTime(generatedAt);
    const timeTotalWidth = doc.getTextWidth(timeLabel + " " + timeValue);
    const timeLabelWidth = doc.getTextWidth(timeLabel + " ");
    const timeStartX = (pageWidth / 2) - (timeTotalWidth / 2);
    doc.text(timeLabel, timeStartX, yPosition);
    doc.setFont("helvetica", "normal");
    doc.text(timeValue, timeStartX + timeLabelWidth, yPosition);

    doc.setFont("helvetica", "bold");
    const totalLabel = "Total Employees - ";
    const totalValue = totalEmployees.toString();
    const totalTotalWidth = doc.getTextWidth(totalLabel + " " + totalValue);
    const totalLabelWidth = doc.getTextWidth(totalLabel + " ");
    doc.text(totalLabel, endX - totalTotalWidth, yPosition);
    doc.setFont("helvetica", "normal");
    doc.text(totalValue, endX - totalTotalWidth + totalLabelWidth, yPosition);

    let columns = [];
    let rows = [];

    if (viewMode === "allocation") {
      columns = ["CDL Jobs", "Employees"];
      rows = taskArray.map(item => [item.CDLjob || "", item.countIWOno || "0"]);
    } else if (viewMode === "supervisor") {
      columns = ["Location", "Supervisor", "Total Employees"];
      rows = supervisorArray.map(item => [item.LocCode || "", item.Supervisorname || "", item.countIWOno || "0"]);
    } else {
      columns = ["Description", "No. of Employees"];
      rows = filteredData.map(item => [item.EngSpec || "", item.countIWOno || "0"]);
    }

    if (viewMode === "allocation") {
      rows.push(["Total", totalEmployees.toString()]);
    } else if (viewMode === "supervisor") {
      rows.push(["Total", "", totalEmployees.toString()]);
    } else {
      rows.push(["Total", totalEmployees.toString()]);
    }

    autoTable(doc, {
      head: [columns],
      body: rows,
      startY: 95,
      styles: { fontSize: 12, cellPadding: 5, lineColor: [220, 220, 220], lineWidth: 0.1 },
      headStyles: { fillColor: [21, 76, 121], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 10, halign: 'center', cellPadding: 6 },
      alternateRowStyles: { fillColor: [248, 249, 252] },
      columnStyles: viewMode === "allocation"
        ? { 0: { cellWidth: 147, halign: 'left' }, 1: { cellWidth: 40, halign: 'center' } }
        : viewMode === "supervisor"
        ? { 0: { cellWidth: 50, halign: 'center' }, 1: { cellWidth: 97, halign: 'left' }, 2: { cellWidth: 40, halign: 'center' } }
        : { 0: { cellWidth: 147, halign: 'left' }, 1: { cellWidth: 40, halign: 'center' } },
      margin: { left: 12, right: 10 },
      theme: 'grid',
      tableWidth: 'auto',
      didParseCell: function (data) {
        if (data.row.index === rows.length - 1) {
          data.cell.styles.fillColor = [21, 76, 121];
          data.cell.styles.textColor = 255;
          data.cell.styles.fontStyle = 'bold';
        }
      },
      didDrawPage: function (data) {
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.setFont("helvetica", "italic");
        const footerText = `Generated on ${formatDate(generatedAt)} at ${formatTime(generatedAt)}`;
        doc.text(footerText, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: "center" });
        doc.text(`Page ${data.pageNumber}`, pageWidth - 20, doc.internal.pageSize.getHeight() - 10);
      }
    });

    const viewName = viewMode === "allocation" ? "CDL_Jobs" : viewMode === "supervisor" ? "Supervisor" : "Details";
    doc.save(`Employee_Allocation_${viewName}_${formatDate(selectedDate)}.pdf`);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <PageContainer>
        <Typography
          sx={{
            fontSize: "18px",
            fontWeight: 700,
            color: "#0e3c66",
            textAlign: "center",
            mb: 2,
            letterSpacing: "1px",
          }}
        >
          Employees Allocation
        </Typography>

        <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">

          {/* Date — half width mobile, 1/3 desktop */}
          <Grid item xs={6} sm={4}>
            <Button
              variant="contained"
              startIcon={<CalendarTodayIcon />}
              onClick={() => setOpenDatePicker(true)}
              sx={{
                width: "100%",
                height: "56px",
                borderRadius: "20px",
                backgroundColor: "#142c54",
                color: "#fff",
                fontWeight: "bold",
                textTransform: "none",
                fontSize: { xs: "13px", sm: "16px" },
                "&:hover": { backgroundColor: "#0D47A1" },
              }}
            >
              {formatDate(selectedDate)}
            </Button>
          </Grid>

          {/* View Type Dropdown — full width mobile, 1/3 desktop */}
          <Grid item xs={12} sm={4} order={{ xs: 3, sm: 2 }}>
            <FormControl fullWidth>
              <InputLabel
                sx={{
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                  color: "#154C79",
                }}
              >
                View Type
              </InputLabel>
              <Select
                value={viewMode}
                onChange={handleViewModeChange}
                label="View Type"
                sx={{
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                  borderRadius: "20px",
                  backgroundColor: "#F0F4F8",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                  height: "56px",
                  "& .MuiSelect-icon": { color: "#154C79" },
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#154C79" },
                }}
                MenuProps={{
                  PaperProps: { sx: { maxHeight: 250, overflowY: "auto" } },
                }}
              >
                <MenuItem value="allocation" sx={{ whiteSpace: "normal", wordWrap: "break-word", padding: "8px 16px", fontWeight: "400", fontSize: "14px", lineHeight: "1.5", "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.08)" }, "&.Mui-selected": { backgroundColor: "rgba(0, 0, 0, 0.12)" } }}>
                  Summary - CDL Jobs
                </MenuItem>
                <MenuItem value="supervisor" sx={{ whiteSpace: "normal", wordWrap: "break-word", padding: "8px 16px", fontWeight: "400", fontSize: "14px", lineHeight: "1.5", "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.08)" }, "&.Mui-selected": { backgroundColor: "rgba(0, 0, 0, 0.12)" } }}>
                  Summary - Supervisor
                </MenuItem>
                <MenuItem value="details" sx={{ whiteSpace: "normal", wordWrap: "break-word", padding: "8px 16px", fontWeight: "400", fontSize: "14px", lineHeight: "1.5", "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.08)" }, "&.Mui-selected": { backgroundColor: "rgba(0, 0, 0, 0.12)" } }}>
                  Details
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Total Employees — half width mobile, 1/3 desktop */}
          <Grid item xs={6} sm={4} order={{ xs: 2, sm: 3 }}>
            <Box
              sx={{
                background: "#e8f0fa",
                border: "1px solid #bbd0ea",
                borderRadius: "20px",
                height: "56px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: { xs: "13px", sm: "15px" },
                color: "#0E3C66",
              }}
            >
              Total: {totalEmployees}
            </Box>
          </Grid>
        </Grid>

        {openDatePicker && (
          <DatePicker
            open={openDatePicker}
            value={selectedDate}
            onChange={handleDateChange}
            onClose={() => setOpenDatePicker(false)}
            renderInput={(params) => (
              <TextField {...params} sx={{ display: "none" }} />
            )}
          />
        )}

        {/* Export PDF */}
        {!loading && (
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExportPDF}
              disabled={loading || !data?.length}
              sx={{
                width: "100%",
                height: "56px",
                borderRadius: "20px",
                backgroundColor: "#2e7d32",
                color: "#fff",
                fontWeight: "bold",
                textTransform: "none",
                fontSize: { xs: "13px", sm: "16px" },
                "&:hover": { backgroundColor: "#1b5e20" },
                "&:disabled": { backgroundColor: "#cccccc", color: "#666666" },
              }}
            >
              Export PDF
            </Button>
          </Box>
        )}

        {/* Search Field */}
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            placeholder={
              viewMode === "allocation"
                ? "Search CDL Jobs..."
                : viewMode === "supervisor"
                ? "Search Supervisors..."
                : "Search Details..."
            }
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#154C79" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "20px",
                height: "56px",
                fontSize: "16px",
              },
            }}
          />
        </Box>

        {/* Table */}
        {loading ? (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress sx={{ color: "#154C79" }} />
          </Box>
        ) : (
          <StyledTableContainer component={Paper}>
            <Table sx={{ minWidth: { xs: "100%", sm: 500 } }}>
              <TableHead>
                <TableRow>
                  {viewMode === "allocation" && (
                    <>
                      <StyledTableCell sx={{ textAlign: "center" }}>CDL JOBS</StyledTableCell>
                      <StyledTableCell sx={{ textAlign: "center" }}>Employees</StyledTableCell>
                    </>
                  )}
                  {viewMode === "supervisor" && (
                    <>
                      <StyledTableCell sx={{ textAlign: "left" }}>Location</StyledTableCell>
                      <StyledTableCell sx={{ textAlign: "left" }}>Supervisor</StyledTableCell>
                      <StyledTableCell sx={{ textAlign: "center" }}>Total Employees</StyledTableCell>
                    </>
                  )}
                  {viewMode === "details" && (
                    <>
                      <StyledTableCell sx={{ textAlign: "left" }}>Description</StyledTableCell>
                      <StyledTableCell>No. of Employees</StyledTableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {currentData.length > 0 ? (
                  currentData.map((row, index) => (
                    <StyledTableRow key={index}>
                      {viewMode === "allocation" && (
                        <>
                          <StyledTableCell sx={{ textAlign: "left", whiteSpace: "normal", wordBreak: "break-word" }}>
                            {row.CDLjob}
                          </StyledTableCell>
                          <StyledTableCell sx={{ fontWeight: 600 }}>{row.countIWOno}</StyledTableCell>
                        </>
                      )}
                      {viewMode === "supervisor" && (
                        <>
                          <StyledTableCell sx={{ textAlign: "left", whiteSpace: "normal", wordBreak: "break-word" }}>
                            {row.LocCode}
                          </StyledTableCell>
                          <StyledTableCell sx={{ textAlign: "left", whiteSpace: "normal", wordBreak: "break-word" }}>
                            {row.Supervisorname}
                          </StyledTableCell>
                          <StyledTableCell sx={{ fontWeight: 600 }}>{row.countIWOno}</StyledTableCell>
                        </>
                      )}
                      {viewMode === "details" && (
                        <>
                          <StyledTableCell sx={{ textAlign: "left", whiteSpace: "normal", wordBreak: "break-word" }}>
                            {row.EngSpec}
                          </StyledTableCell>
                          <StyledTableCell sx={{ fontWeight: 600 }}>{row.countIWOno}</StyledTableCell>
                        </>
                      )}
                    </StyledTableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={viewMode === "details" ? 2 : viewMode === "supervisor" ? 3 : 2}
                      align="center"
                      sx={{ fontSize: "14px" }}
                    >
                      No records found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </StyledTableContainer>
        )}
      </PageContainer>
    </LocalizationProvider>
  );
}